import React from "react";
import Page from "../components/Page";
import { CircularProgress, Grid } from "@mui/material";
import NormalText from "../Texts/NormalText";
import useAppDispatch from "../redux/useAppDispatch";
import useOpenSnackbar from "../redux/useOpenSnackbar";
import useAppSelector from "../redux/useAppSelector";
import {
  clickFavoriteExercise,
  getAccueilPrincipalVideo,
  getPopularExercises,
  getStartedExercises,
  IVideo,
  updateVideo,
} from "../redux/exercisesSlice";
import { DrawCard, DrawExerciseCard, videoTypes } from "./Lecons";
import {
  clickFavorite,
  getPopularLecons,
  getStartedLecons,
} from "../redux/leconsSlice";
import Sizes from "../utils/Sizes";
import ButtonIconWithTooltip from "../components/ButtonIconWithTooltip";
import { EditRounded } from "@mui/icons-material";
import Colors from "../utils/Colors";
import { GenericPopup } from "../components/Popup/GenericPopup";
import BasicInput from "../components/BasicInput";
import SecondaryButton from "../components/SecondaryButton/SecondaryButton";
import PrimaryButton from "../components/PrimaryButton/PrimaryButton";
import makeAPIRequest from "../redux/makeAPIRequest";
const VideoPlayer = ({
  videoSrc,
  title,
}: {
  videoSrc: string;
  title: string;
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [thumbnail, setThumbnail] = React.useState<string | undefined>(
    undefined
  );
  const [isPlaying, setIsPlaying] = React.useState(false); // État pour cacher le titre

  React.useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = 2; // Capture à 2 secondes
      video.onloadeddata = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          setThumbnail(canvas.toDataURL("image/png")); // Convertir en base64
        }
        video.currentTime = 0; // Retour à 0 seconde
      };
    }
  }, [videoSrc]);

  return (
    <Grid container direction="column" wrap="nowrap" gap={"5px"}>
      <Grid
        item
        display="grid"
        sx={{ position: "relative", width: "fit-content" }}
      >
        {/* Titre superposé, caché quand la vidéo est en lecture */}
        {!isPlaying && thumbnail && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0, 0, 0, 0.5)", // Fond semi-transparent
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.2rem",
              fontWeight: "bold",
              textAlign: "center",
              borderRadius: "8px",
              pointerEvents: "none", // Empêche l'interaction avec le texte
              transition: "opacity 0.3s ease-in-out",
            }}
          >
            {title}
          </div>
        )}

        {/* Vidéo avec aperçu */}
        <video
          ref={videoRef}
          controls
          //crossOrigin="anonymous"
          poster={thumbnail}
          onPlay={() => setIsPlaying(true)} // Cache le texte quand la vidéo démarre
          onPause={() => setIsPlaying(false)} // Réaffiche le texte si la vidéo est en pause
          style={{
            maxHeight: "300px",
            maxWidth: "300px",
            objectFit: "cover",
            borderRadius: "8px",
          }}
        >
          <source src={videoSrc} type="video/mp4" />
          Votre navigateur ne supporte pas la vidéo.
        </video>
      </Grid>
    </Grid>
  );
};

const EditAccueilVideoPopup: React.FC<{
  onClose: () => void;
  video?: IVideo;
}> = (props) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [refreshVideo, setRefreshVideo] = React.useState<boolean>(false);
  const [video, setVideo] = React.useState<IVideo | undefined>(undefined);
  const snackbarOpenUse = useOpenSnackbar();

  React.useEffect(() => {
    if (props.video) {
      setVideo(props.video);
    }
  }, [props.video]);

  if (!video) {
    return <></>;
  }
  return (
    <GenericPopup
      xs={6}
      onClose={props.onClose}
      title={{
        leftPart: "Modifier",
        rightPart: "ma vidéo",
      }}
    >
      <Grid
        container
        direction="column"
        wrap="nowrap"
        padding="20px"
        gap="20px"
        alignItems={"center"}
      >
        <Grid item display="grid">
          <Grid container direction="column" wrap="nowrap" gap="15px">
            <Grid item xs display="grid">
              <BasicInput
                title="Titre"
                colorTitle={"Black"}
                placeholder=""
                style={{
                  maxWidth: "300px",
                }}
                value={video?.title}
                onChange={(e) => {
                  setVideo((a) => {
                    if (a) {
                      return { ...a, title: e.target.value };
                    }
                    return undefined;
                  });
                }}
              />
            </Grid>
            {refreshVideo && (
              <Grid
                item
                display={"grid"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <CircularProgress
                  style={{
                    color: Colors.Black,
                  }}
                />
              </Grid>
            )}
            {!refreshVideo && (
              <Grid item display="grid">
                <Grid
                  container
                  direction="column"
                  wrap="nowrap"
                  gap="10px"
                  alignItems={"center"}
                >
                  <Grid item display="grid">
                    <input
                      type="file"
                      name="file-video"
                      id="file-video"
                      accept="video/*"
                      style={{
                        opacity: 0,
                        zIndex: -1,
                        position: "absolute",
                      }}
                      onChange={(e) => {
                        const fileReader = new FileReader();

                        if (e?.target?.files?.[0]) {
                          if (e.target.files[0].size >= 400000000) {
                            snackbarOpenUse.error("Le fichier est trop lourd.");
                          } else if (
                            !videoTypes.includes(e.target.files[0].type)
                          ) {
                            snackbarOpenUse.error(
                              "Le fichier est au mauvais format, ça doit être une vidéo."
                            );
                          } else {
                            fileReader.readAsDataURL(e.target.files[0]);
                            fileReader.onload = (event) => {
                              const attachment = event?.target
                                ?.result as string;
                              //const attachment_name = e?.target?.files?.[0]
                              //  ?.name as string;
                              // Add audio title
                              setRefreshVideo(true);
                              setTimeout(() => {
                                setRefreshVideo(false);
                              }, 1000);
                              setVideo((v) => {
                                return {
                                  ...v,
                                  video: attachment,
                                } as any;
                              });
                            };
                          }
                        }
                      }}
                    />
                    <label htmlFor="file-video">
                      <NormalText
                        text="Charger une nouvelle vidéo"
                        fontSize="12px"
                        fontWeight={600}
                        color="Black"
                        className="button-nobreak"
                      />
                    </label>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {!refreshVideo && video?.video && (
              <Grid
                item
                display="grid"
                justifySelf={"center"}
                alignItems={"center"}
                alignSelf={"center"}
              >
                <VideoPlayer
                  title={video?.title as any}
                  videoSrc={video?.video as any}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        {!refreshVideo && (
          <Grid item display="grid">
            {isLoading ? (
              <CircularProgress
                style={{
                  color: Colors.Black,
                }}
              />
            ) : (
              <Grid
                container
                direction="row"
                gap="10px"
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Grid item display="grid">
                  <SecondaryButton
                    disabled={isLoading}
                    text={"Annuler"}
                    onClick={() => {
                      if (props.onClose) {
                        props.onClose();
                      }
                    }}
                  />
                </Grid>
                <Grid item display="grid">
                  <PrimaryButton
                    disabled={isLoading}
                    text={"Mettre à jour"}
                    onClick={() => {
                      setIsLoading(true);
                      dispatch(
                        updateVideo({
                          id: video._id?.toString() as string,
                          data: video,
                        })
                      )
                        .unwrap()
                        .then(() => {
                          dispatch(getAccueilPrincipalVideo())
                            .unwrap()
                            .then(() => {
                              if (props.onClose) {
                                props.onClose();
                              }
                            })
                            .catch(() => {
                              snackbarOpenUse.error(
                                "Impossible de mettre à jour la vidéo principale."
                              );
                            })
                            .finally(() => {
                              setIsLoading(false);
                              return;
                            });
                        })
                        .catch(() => {
                          snackbarOpenUse.error(
                            "Impossible de récupérer la vidéo principale."
                          );
                          setIsLoading(false);
                        })
                        .finally(() => {
                          return;
                        });
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </GenericPopup>
  );
};

const Accueil: React.FC = () => {
  const snackbarUse = useOpenSnackbar();
  const dispatch = useAppDispatch();
  const popularExercices = useAppSelector((x) => x.exercises.popular);
  const startedExercices = useAppSelector((x) => x.exercises.started);
  const startedLecons = useAppSelector((x) => x.lecons.started);
  const popularLecons = useAppSelector((x) => x.lecons.popular);
  const accueilPrincipalVideo = useAppSelector(
    (x) => x.exercises.accueilPrincipalVideo
  );
  const pageExerciseLoading = useAppSelector((x) => x.exercises.isLoading);
  const pageLeconLoading = useAppSelector((x) => x.lecons.isLoading);
  const pageLoading = pageExerciseLoading || pageLeconLoading;
  const role = useAppSelector((x) => x?.user?.user?.role);
  const isAdmin = role === "admin" ? true : false;
  const [editVideoOpen, setEditVideoOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    dispatch(getStartedLecons())
      .unwrap()
      .then((res) => {
        return;
      })
      .catch((err) => {
        snackbarUse.error("Impossible de récupérer les leçons commencés.");
      })
      .finally(() => {
        return;
      });
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(getStartedExercises())
      .unwrap()
      .then((res) => {
        return;
      })
      .catch((err) => {
        snackbarUse.error("Impossible de récupérer les exercices commencés.");
      })
      .finally(() => {
        return;
      });
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(getPopularLecons())
      .unwrap()
      .then((res) => {
        return;
      })
      .catch((err) => {
        snackbarUse.error("Impossible de récupérer les leçons populaires.");
      })
      .finally(() => {
        return;
      });
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(getPopularExercises())
      .unwrap()
      .then((res) => {
        return;
      })
      .catch((err) => {
        snackbarUse.error("Impossible de récupérer les exercices populaires.");
      })
      .finally(() => {
        return;
      });
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(getAccueilPrincipalVideo())
      .unwrap()
      .then((res) => {
        return;
      })
      .catch((err) => {
        snackbarUse.error("Impossible de récupérer la vidéo principale.");
      })
      .finally(() => {
        return;
      });
  }, [dispatch]);

  React.useEffect(() => {});

  return (
    <>
      {editVideoOpen && (
        <EditAccueilVideoPopup
          onClose={() => setEditVideoOpen(false)}
          video={accueilPrincipalVideo}
        />
      )}
      <Page pageTitle="Accueil" noPadding={true}>
        {pageLoading ? (
          <>
            <Grid
              container
              direction="row"
              wrap="nowrap"
              justifyContent={"center"}
              alignItems={"center"}
              paddingTop="20px"
            >
              <CircularProgress
                style={{
                  color: "white",
                }}
              />
            </Grid>
          </>
        ) : (
          <Grid
            container
            direction="column"
            wrap="nowrap"
            gap="30px"
            style={{
              paddingLeft: "15px",
              paddingTop: "20px",
              paddingBottom: "15px",
            }}
          >
            {accueilPrincipalVideo && (
              <Grid
                item
                display="grid"
                justifySelf={"flex-start"}
                alignSelf={"flex-start"}
              >
                <Grid container direction="column" wrap="nowrap">
                  <Grid item display="grid">
                    <Grid
                      container
                      direction="row"
                      wrap="nowrap"
                      gap="10px"
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      <Grid item display="grid">
                        <NormalText
                          text="Stoic System"
                          fontSize={Sizes.large}
                          fontWeight={900}
                        />
                      </Grid>
                      <Grid item display="grid">
                        <ButtonIconWithTooltip
                          Icon={
                            <EditRounded
                              style={{
                                color: Colors.White,
                                width: "20px",
                                height: "20px",
                              }}
                            />
                          }
                          onClick={() => {
                            setEditVideoOpen(true);
                          }}
                          tooltipText="Modifier"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item display="grid" paddingTop="15px">
                    <VideoPlayer
                      videoSrc={accueilPrincipalVideo.video as string}
                      title={accueilPrincipalVideo.title}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {popularLecons?.length > 0 && (
              <Grid item display="grid">
                <Grid container direction="column" wrap="nowrap">
                  <Grid item display="grid">
                    <NormalText
                      text="Leçons populaires"
                      fontSize={Sizes.large}
                      fontWeight={900}
                    />
                  </Grid>
                  <Grid item display="grid">
                    <Grid
                      container
                      direction="row"
                      gap="20px"
                      paddingRight="15px"
                      paddingBottom="15px"
                      paddingTop="15px"
                      wrap="nowrap"
                      overflow={"auto"}
                    >
                      {popularLecons.map((card) => {
                        return (
                          <Grid item key={card._id} display="grid">
                            <DrawCard
                              card={card}
                              onFavoriteClick={() => {
                                if (card?._id) {
                                  dispatch(clickFavorite(card._id));
                                }
                              }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {popularExercices?.length > 0 && (
              <Grid item display="grid">
                <Grid container direction="column" wrap="nowrap">
                  <Grid item display="grid">
                    <NormalText
                      text="Exercices populaires"
                      fontSize={Sizes.large}
                      fontWeight={900}
                    />
                  </Grid>
                  <Grid item display="grid">
                    <Grid
                      container
                      direction="row"
                      gap="20px"
                      paddingRight="15px"
                      paddingBottom="15px"
                      paddingTop="15px"
                      wrap="nowrap"
                      overflow={"auto"}
                    >
                      {popularExercices.map((card) => {
                        return (
                          <Grid item key={card._id} display="grid">
                            <DrawExerciseCard
                              card={card}
                              onEditClick={undefined}
                              onDeleteClick={undefined}
                              onFavoriteClick={() => {
                                if (card?._id) {
                                  dispatch(clickFavoriteExercise(card._id));
                                }
                              }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {startedLecons?.length > 0 && (
              <Grid item display="grid">
                <Grid container direction="column" wrap="nowrap">
                  <Grid item display="grid">
                    <NormalText
                      text="Reprendre mes leçons"
                      fontSize={Sizes.large}
                      fontWeight={400}
                    />
                  </Grid>
                  <Grid item display="grid">
                    <Grid
                      container
                      direction="row"
                      gap="20px"
                      paddingRight="15px"
                      paddingBottom="15px"
                      paddingTop="15px"
                      wrap="nowrap"
                      overflow={"auto"}
                    >
                      {startedLecons.map((card) => {
                        return (
                          <Grid item key={card._id} display="grid">
                            <DrawCard
                              card={card}
                              onFavoriteClick={() => {
                                if (card?._id) {
                                  dispatch(clickFavorite(card._id));
                                }
                              }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {startedExercices?.length > 0 && (
              <Grid item display="grid">
                <Grid container direction="column" wrap="nowrap">
                  <Grid item display="grid">
                    <NormalText
                      text="Reprendre mes exercices"
                      fontSize={Sizes.large}
                      fontWeight={900}
                    />
                  </Grid>
                  <Grid item display="grid">
                    <Grid
                      container
                      direction="row"
                      gap="20px"
                      paddingRight="15px"
                      paddingBottom="15px"
                      paddingTop="15px"
                      wrap="nowrap"
                      overflow={"auto"}
                    >
                      {startedExercices.map((card) => {
                        return (
                          <Grid item key={card._id} display="grid">
                            <DrawExerciseCard
                              card={card}
                              onEditClick={undefined}
                              onDeleteClick={undefined}
                              onFavoriteClick={() => {
                                if (card?._id) {
                                  dispatch(clickFavoriteExercise(card._id));
                                }
                              }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Page>
    </>
  );
};

export default Accueil;
